/**
 * PremiumDashboardPage.jsx
 * Dashboard Premium de Teste usando componentes Premium
 *
 * Acesso: /dashboard-premium
 */

import {
 useState, useMemo, useEffect, useCallback 
} from 'react';
import {
 Box, Typography, Paper, Grid, Chip, IconButton, Tooltip 
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '../common/components/LocalizationProvider';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';

// Premium Components
import {
  StatCard,
  StatCardGrid,
  StatusDistribution,
  DashboardWidget,
  PremiumLineChart,
  PremiumBarChart,
  PremiumActivityFeed,
  FadeIn,
  SlideUp,
  AnimatedCounter,
} from '../common/components/premium';

const PremiumDashboardPage = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const tokens = usePremiumTokens();

  // State
  const [devices, setDevices] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [weeklyData, setWeeklyData] = useState([]);

  // Fetch devices and positions
  const fetchData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      else setRefreshing(true);

      const [devicesRes, positionsRes] = await Promise.all([
        fetch('/api/devices'),
        fetch('/api/positions'),
      ]);

      if (devicesRes.ok && positionsRes.ok) {
        const devicesData = await devicesRes.json();
        const positionsData = await positionsRes.json();

        setDevices(devicesData);
        const posMap = {};
        positionsData.forEach((pos) => {
          posMap[pos.deviceId] = pos;
        });
        setPositions(posMap);
        setLastUpdate(new Date());
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch recent events
  const fetchRecentEvents = useCallback(async () => {
    if (devices.length === 0) return;

    setLoadingEvents(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);

      // Limit to first 50 devices
      const deviceIds = devices.slice(0, 50).map((d) => `deviceId=${d.id}`).join('&');

      const response = await fetch(
        `/api/reports/events?from=${from.toISOString()}&to=${to.toISOString()}&${deviceIds}`,
        { headers: { Accept: 'application/json' } }
      );

      if (response.ok) {
        const data = await response.json();
        const eventsWithNames = data
          .slice(0, 30)
          .map((event) => {
            const device = devices.find((d) => d.id === event.deviceId);
            return {
              ...event,
              deviceName: device?.name || `Device ${event.deviceId}`,
            };
          })
          .sort((a, b) => new Date(b.serverTime || b.eventTime) - new Date(a.serverTime || a.eventTime));
        setRecentEvents(eventsWithNames);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoadingEvents(false);
    }
  }, [devices]);

  // Generate mock weekly data (in a real app, this would come from API)
  const generateWeeklyData = useCallback(() => {
    const days = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const today = new Date().getDay();
    const data = [];

    for (let i = 6; i >= 0; i--) {
      const dayIndex = (today - i + 7) % 7;
      data.push({
        day: days[dayIndex],
        km: Math.floor(Math.random() * 5000) + 1000,
        viagens: Math.floor(Math.random() * 200) + 50,
      });
    }
    setWeeklyData(data);
  }, []);

  // Initial load
  useEffectAsync(async () => {
    await fetchData(true);
    generateWeeklyData();
  }, []);

  // Fetch events when devices load
  useEffect(() => {
    if (devices.length > 0) {
      fetchRecentEvents();
    }
  }, [devices.length, fetchRecentEvents]);

  // Polling every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // KPIs calculation
  const kpis = useMemo(() => {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    let online = 0;
    let offline = 0;
    let alerts = 0;

    devices.forEach((device) => {
      const pos = positions[device.id];
      const lastTime = pos ? new Date(pos.fixTime).getTime() : 0;
      const isOnline = now - lastTime < fiveMinutes;

      if (isOnline) {
        online++;
      } else {
        offline++;
      }

      if (pos?.attributes?.alarm || pos?.attributes?.batteryLevel < 20) {
        alerts++;
      }
    });

    return { total: devices.length, online, offline, alerts };
  }, [devices, positions]);

  // Status distribution data - usando cores do design system premium
  const statusDistributionData = useMemo(() => [
    {
      status: 'online',
      label: 'Online',
      count: kpis.online,
      color: tokens?.status?.online || tokens?.semantic?.success || '#00d4aa',
    },
    {
      status: 'offline',
      label: 'Offline',
      count: kpis.offline,
      color: tokens?.status?.offline || tokens?.semantic?.error || '#6e7681',
    },
  ], [kpis, tokens]);

  // Category distribution
  const categoryDistribution = useMemo(() => {
    const categories = {};
    devices.forEach((device) => {
      const cat = device.category || 'outros';
      categories[cat] = (categories[cat] || 0) + 1;
    });
    return Object.entries(categories)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [devices]);

  const handleEventClick = (event) => {
    if (event.deviceId) {
      // Navegar para /eventmap com período de 24h centrado no evento
      const eventTime = new Date(event.eventTime || event.serverTime);
      const from = new Date(eventTime.getTime() - 12 * 60 * 60 * 1000).toISOString(); // 12h antes
      const to = new Date(eventTime.getTime() + 12 * 60 * 60 * 1000).toISOString(); // 12h depois
      navigate(`/eventmap?deviceId=${event.deviceId}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
    }
  };

  // Detecta dark/light mode para fallbacks corretos
  const isDark = tokens?.isDark !== false;
  const surfaceBase = tokens?.surface?.base || (isDark ? '#0d1117' : '#f6f8fa');
  const surfaceElevated = tokens?.surface?.elevated || (isDark ? '#161b22' : '#ffffff');
  const textPrimary = tokens?.text?.primary || (isDark ? '#f0f6fc' : '#1f2328');
  const textSecondary = tokens?.text?.secondary || (isDark ? '#8b949e' : '#656d76');
  const borderSubtle = tokens?.border?.subtle || (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.08)');

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 3, minHeight: '100vh', bgcolor: surfaceBase }}>
        <StatCardGrid columns={4}>
          {[1, 2, 3, 4].map((i) => (
            <StatCard key={i} loading title="" value="" />
          ))}
        </StatCardGrid>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100vh',
        bgcolor: surfaceBase,
        color: textPrimary,
      }}
    >
      {/* Header */}
      <FadeIn>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: textPrimary, background: 'transparent' }}>
              Dashboard Premium
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary, mt: 0.5, background: 'transparent' }}>
              Visão geral da frota em tempo real
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {lastUpdate && (
              <Chip
                size="small"
                label={`Atualizado: ${lastUpdate.toLocaleTimeString()}`}
                sx={{
                  bgcolor: surfaceElevated,
                  color: textSecondary,
                  '& .MuiChip-label': { background: 'transparent' },
                }}
              />
            )}
            <Tooltip title="Atualizar">
              <IconButton
                onClick={() => fetchData(false)}
                disabled={refreshing}
                sx={{ color: textPrimary }}
              >
                <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </FadeIn>

      {/* KPIs */}
      <SlideUp delay={100}>
        <StatCardGrid columns={4} gap={2}>
          <StatCard
            title="Total de Veículos"
            value={<AnimatedCounter value={kpis.total} />}
            icon={<LocalShippingIcon />}
            color="primary"
            subtitle={`${kpis.total} dispositivos cadastrados`}
          />
          <StatCard
            title="Online"
            value={<AnimatedCounter value={kpis.online} />}
            icon={<GpsFixedIcon />}
            color="success"
            trend={kpis.total > 0 ? {
              value: Math.round((kpis.online / kpis.total) * 100),
              direction: 'up',
            } : undefined}
            subtitle={kpis.total > 0 ? `${Math.round((kpis.online / kpis.total) * 100)}% da frota` : ''}
          />
          <StatCard
            title="Offline"
            value={<AnimatedCounter value={kpis.offline} />}
            icon={<GpsOffIcon />}
            color="error"
            trend={kpis.total > 0 && kpis.offline > 0 ? {
              value: Math.round((kpis.offline / kpis.total) * 100),
              direction: 'down',
            } : undefined}
            subtitle={kpis.total > 0 ? `${Math.round((kpis.offline / kpis.total) * 100)}% da frota` : ''}
          />
          <StatCard
            title="Alertas"
            value={<AnimatedCounter value={kpis.alerts} />}
            icon={<WarningAmberIcon />}
            color="warning"
            onClick={() => navigate('/reports/events')}
            subtitle="Clique para ver detalhes"
          />
        </StatCardGrid>
      </SlideUp>

      {/* Main Content */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Left Column - Charts */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <SlideUp delay={200}>
            <Grid container spacing={3}>
              {/* Weekly Distance Chart */}
              <Grid size={12}>
                <DashboardWidget title="Km Rodados (Últimos 7 dias)">
                  <PremiumLineChart
                    data={weeklyData}
                    xKey="day"
                    lines={[
                      { dataKey: 'km', name: 'Quilômetros', color: tokens?.accent?.primary },
                    ]}
                    height={250}
                    yAxisUnit=" km"
                    tooltipFormatter={(value) => `${value.toLocaleString()} km`}
                  />
                </DashboardWidget>
              </Grid>

              {/* Status Distribution & Category */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardWidget title="Status da Frota">
                  <StatusDistribution
                    data={statusDistributionData}
                    variant="bar"
                    showLegend
                    showPercentage
                  />
                </DashboardWidget>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardWidget title="Veículos por Categoria">
                  <PremiumBarChart
                    data={categoryDistribution}
                    xKey="name"
                    bars={[{ dataKey: 'count', name: 'Quantidade' }]}
                    height={200}
                    showLegend={false}
                  />
                </DashboardWidget>
              </Grid>
            </Grid>
          </SlideUp>
        </Grid>

        {/* Right Column - Activity Feed */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <SlideUp delay={300}>
            <Paper
              sx={{
                p: 2,
                bgcolor: surfaceElevated,
                border: `1px solid ${borderSubtle}`,
                borderRadius: tokens?.radius?.lg || 12,
                height: 'calc(100vh - 320px)',
                minHeight: 400,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: textPrimary, background: 'transparent' }}>
                  Eventos Recentes
                </Typography>
                <Chip
                  size="small"
                  label={recentEvents.length}
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <PremiumActivityFeed
                  events={recentEvents}
                  loading={loadingEvents}
                  onEventClick={handleEventClick}
                  groupByDay
                  showDevice
                  maxHeight="100%"
                  emptyMessage="Nenhum evento nas últimas 24h"
                />
              </Box>
            </Paper>
          </SlideUp>
        </Grid>
      </Grid>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default PremiumDashboardPage;
